import {
  Component,
  EventEmitter,
  OnInit,
  Output,
  computed,
  effect,
  inject,
  input,
  signal,
  untracked,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { SelectModule } from 'primeng/select';
import { ConversionEngineService } from '../../../services/mbrs/conversion-engine.service';
import type { ConversionEngineOption } from '../../../../types/conversion-engine';

/**
 * Resolves which engine a freshly-mounted picker should show, in priority
 * order: the engine that last processed this conversion, then the server's
 * configured default, then the first option.
 *
 * Exported for direct testing: the "no option carries isDefault" and "several
 * do" cases are backend states that are awkward to drive through the template
 * but perfectly possible on the wire.
 *
 * `lastUsedEngine` is matched against the CURRENT option list on purpose. A
 * conversion can record an engine that has since been retired (v1.5 is
 * documented backend-side as temporary); preselecting a value with no matching
 * option would leave the control blank.
 */
export function resolveSelection(
  options: readonly ConversionEngineOption[],
  lastUsedEngine: string | null,
): string | null {
  if (options.length === 0) {
    return null;
  }
  const previouslyUsed = options.find((option) => option.value === lastUsedEngine);
  if (previouslyUsed) {
    return previouslyUsed.value;
  }
  const serverDefault = options.find((option) => option.isDefault);
  return serverDefault ? serverDefault.value : options[0].value;
}

/**
 * Lets the user choose which conversion engine processes their filing.
 *
 * Mounted in two places, both of which project it into the slot that
 * `FilingActionButtonsComponent` exposes or place it beside the dashboard's
 * Convert to XBRL button:
 *
 *  - Dashboard — chooses the engine for Phase 1 prerequisite extraction on a
 *    brand-new conversion. No history, so `lastUsedEngine` is null.
 *  - Filing form, role 0 and role 1 — chooses the engine for the Phase 2 run
 *    that Submit kicks off.
 *
 * Renders NOTHING when the service's option list is empty. That single
 * condition covers both the flag-off case and the fetch-failure case, which is
 * why there is no separate branch for either: in both, submits omit the engine
 * field and the server applies its configured default.
 *
 * The component is deliberately stateless beyond its own selection. The pick is
 * component-local and resets when the host unmounts — on the filing form the
 * container's `*ngIf` destroys the sheet on every sheet switch, so a pick does
 * not survive navigating away and back. That is an accepted, QA-communicated
 * limitation, chosen over both blocking navigation while Filing Info is dirty
 * and persisting a transient submit-time choice as if it were filing data.
 */
@Component({
  selector: 'app-conversion-engine-picker',
  standalone: true,
  imports: [CommonModule, FormsModule, SelectModule],
  templateUrl: './conversion-engine-picker.component.html',
  styleUrls: ['./conversion-engine-picker.component.css'],
})
export class ConversionEnginePickerComponent implements OnInit {
  private readonly engineService = inject(ConversionEngineService);

  /**
   * Host-computed. `false` on the dashboard and on role 0 (Filing
   * Prerequisites); `!hasUnsubmittedChanges()` on role 1 (Filing Information),
   * because a Phase 2 conversion only re-runs when Filing Info actually
   * changed.
   */
  readonly disabled = input<boolean>(false);

  /**
   * The engine that last processed this conversion, or null when there is no
   * history (a brand-new conversion at the dashboard). Drives both the
   * preselection and the hint line.
   */
  readonly lastUsedEngine = input<string | null>(null);

  /** The selected wire value, or null while nothing is resolved. */
  @Output() readonly engineChange = new EventEmitter<string | null>();

  readonly engineOptions = computed(() => this.engineService.options());

  private readonly selection = signal<string | null>(null);
  readonly selectedEngine = this.selection.asReadonly();

  /**
   * Last value `resolveSelection` produced, held as a PLAIN field rather than a
   * signal on purpose.
   *
   * The effect below must react to the option list and the conversion's
   * history, and to nothing else. Guarding on `this.selection()` instead would
   * make the effect depend on its own output: a user's pick would retrigger it,
   * re-resolve to the default, and silently revert the choice.
   */
  private lastResolved: string | null = null;

  /**
   * Set true the moment the user makes an explicit selection, and never unset.
   *
   * `lastResolved` (above) guards the effect against re-triggering off its OWN
   * output, but it does not guard against an input arriving LATE. `lastUsedEngine`
   * is set from the filing-form container's conversion-listing subscribe, which
   * races the picker's own mount; if it lands AFTER the option list AND after the
   * user has already picked an engine, `resolveSelection` genuinely returns a
   * different value from before — `resolved === this.lastResolved` is false, so
   * the effect does not early-return, and the user's explicit choice is silently
   * replaced. In practice this window is close to unreachable (the listing fetch
   * starts well before `hydrated()`, and no sheet mounts until then), but that
   * ordering is incidental, not enforced, and the failure mode — a silent revert
   * of an explicit user choice — is exactly the class of bug the `lastResolved`
   * guard exists to prevent for the SAME-output case. Once the user has picked,
   * no later input arrival may override it.
   */
  private hasUserPicked = false;

  readonly lastUsedLabel = computed(() => {
    const previouslyUsed = this.engineOptions().find(
      (option) => option.value === this.lastUsedEngine(),
    );
    return previouslyUsed ? previouslyUsed.label : null;
  });

  constructor() {
    // Re-resolves whenever the option list arrives (it is fetched
    // asynchronously, so it is usually empty at first render) or the host
    // learns the conversion's history. Emits so the host's submit payload is
    // correct without the user touching the control.
    //
    // This emits during change detection. Every host therefore stores the
    // value in a field or signal that nothing in its template binds — see
    // `onEngineChange` on role 0, role 1 and the dashboard (role 0/role 1
    // use a plain field; the dashboard's XbrlFormComponent.selectedEngine is
    // a signal — both are fine, since Angular 19 permits a signal write from
    // an effect). A host that bound the emitted value into its own template
    // would get ExpressionChangedAfterItHasBeenCheckedError; store it, do not
    // render it.
    effect(() => {
      if (this.hasUserPicked) {
        return;
      }
      const resolved = resolveSelection(this.engineOptions(), this.lastUsedEngine());
      if (resolved === this.lastResolved) {
        return;
      }
      this.lastResolved = resolved;
      untracked(() => {
        this.selection.set(resolved);
        this.engineChange.emit(resolved);
      });
    });
  }

  ngOnInit(): void {
    // The only caller. `ensureLoaded` no-ops when the runtime flag is off, so
    // a deployment with the feature disabled makes no network call.
    this.engineService.ensureLoaded();
  }

  onSelectionChange(value: string | null): void {
    this.hasUserPicked = true;
    this.selection.set(value);
    this.engineChange.emit(value);
  }
}
